import { Auth0Session, DevelopmentSession } from '@traceio-org/auth-ui';
import { createPlatformClient, type PlatformClient } from '@traceio-org/platform-client';
import type { HubConfig } from './config';
import { SessionStore } from './session-store';

/** Everything the hub talks to. Routes and pages receive it explicitly so tests can replace any part. */
export interface HubDependencies {
  config: HubConfig;
  client: PlatformClient;
  sessions: SessionStore;
  /** The address the hub is served on. */
  location: { hostname: string; origin: string };
  /** Full-page navigation to another origin, used only after checkDestination accepted the URL. */
  assign: (url: string) => void;
  timing: {
    /** Visible pause before continuing to an enforced organization SSO, so the person can cancel. */
    ssoContinueDelayMs: number;
    /** Start your trial: how often the progress screen asks how the setup is going. */
    startPollMs: number;
    /** Start your trial: the pause after a change of the setup before its price is asked for again. */
    startEstimateDelayMs: number;
  };
}

export const SSO_CONTINUE_DELAY_MS = 1500;
export const START_POLL_MS = 2000;
export const START_ESTIMATE_DELAY_MS = 300;

export function createHubDependencies(config: HubConfig): HubDependencies {
  const auth0 = config.auth0
    ? new Auth0Session({
        domain: config.auth0.domain,
        clientId: config.auth0.clientId,
        audience: config.auth0.audience,
      })
    : null;
  // The client asks the session store for tokens at call time, so it is created before the store.
  let sessions: SessionStore | null = null;
  const client = createPlatformClient({
    baseUrl: config.platformApiUrl,
    getAccessToken: () => {
      if (!sessions) throw new Error('account-hub: session store is not ready');
      return sessions.getAccessToken();
    },
  });
  // Development identity exists only in development and test builds, whatever the API reports.
  const dev = config.devIdentityAllowed
    ? new DevelopmentSession({ client, defaultAudience: config.platformAudience })
    : null;
  sessions = new SessionStore(auth0, dev, config.platformAudience);
  return {
    config,
    client,
    sessions,
    location: { hostname: window.location.hostname, origin: window.location.origin },
    assign: (url) => window.location.assign(url),
    timing: {
      ssoContinueDelayMs: SSO_CONTINUE_DELAY_MS,
      startPollMs: START_POLL_MS,
      startEstimateDelayMs: START_ESTIMATE_DELAY_MS,
    },
  };
}
